<script lang="ts">
  import { Link } from "@inertiajs/svelte";
  import type { Trip, Settlement } from "../lib/types";
  import { settlementApi } from "../lib/api";
  import { addNotification } from "../lib/stores";
  import EmptyState from "../components/feedback/EmptyState.svelte";
  import Layout from "../components/layout/Layout.svelte";
  import TripSubNav from "../components/navigation/TripSubNav.svelte";
  import Button from "../components/input/Button.svelte";
  import { formatCurrency } from "../lib/utils/format";

  let { trip, settlement = null, user = null }: {
    trip: Trip;
    settlement?: Settlement | null;
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  let loading = $state(false);
  let downloading = $state(false);

  // ========== 다운로드 기능 ==========
  async function downloadSettlementPDF() {
    downloading = true;
    try {
      // 더미 PDF 다운로드 - 실제 구현 시 서버에서 PDF 생성
      const response = await fetch(`/api/v1/trips/${trip.id}/settlement/download?format=pdf`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${trip.name}_정산내역.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        addNotification("success", "PDF 다운로드가 시작되었습니다!");
      } else {
        // 더미 폴백 - 텍스트 파일로 다운로드
        downloadAsText();
      }
    } catch {
      downloadAsText();
    } finally {
      downloading = false;
    }
  }

  async function downloadSettlementImage() {
    downloading = true;
    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/settlement/download?format=image`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${trip.name}_정산내역.png`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        addNotification("success", "이미지 다운로드가 시작되었습니다!");
      } else {
        addNotification("info", "이미지 다운로드 기능은 준비 중입니다.");
      }
    } catch {
      addNotification("info", "이미지 다운로드 기능은 준비 중입니다.");
    } finally {
      downloading = false;
    }
  }

  function downloadAsText() {
    // 정산 내역을 텍스트 파일로 다운로드
    const summary = getSettlementSummary();
    const blob = new Blob([summary], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${trip.name}_정산내역.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    addNotification("success", "정산 내역이 다운로드되었습니다!");
  }

  async function completeSettlement() {
    if (!confirm("정산을 완료하시겠습니까? 이 작업은 취소할 수 없습니다.")) {
      return;
    }

    loading = true;
    try {
      await settlementApi.complete(trip.id);
      addNotification("success", "정산이 완료되었습니다!");
      window.location.reload();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        addNotification("error", error.response.data.errors[0]?.message || "정산 처리에 실패했습니다.");
      } else {
        addNotification("error", "정산 처리에 실패했습니다.");
      }
    } finally {
      loading = false;
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
    addNotification("success", "클립보드에 복사되었습니다!");
  }

  // Generate settlement summary text
  function getSettlementSummary(): string {
    if (!settlement) return "";

    let summary = `📊 ${trip.name} 정산 내역\n\n`;
    summary += `총 지출: ₩${formatCurrency(settlement.totalExpenses)}\n`;
    summary += `1인당: ₩${formatCurrency(settlement.perPersonShare)}\n\n`;
    summary += `💸 이체 내역:\n`;

    settlement.transactions.forEach((t) => {
      summary += `${t.from.name} → ${t.to.name}: ₩${formatCurrency(t.amount)}\n`;
    });

    return summary;
  }
</script>

<Layout {user}>
  <div class="min-h-screen bg-app">
    <!-- Trip Sub Navigation -->
    <TripSubNav {trip} currentPage="settlement" />

    <!-- Header -->
    <header class="bg-surface border-b border">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-bold text-primary flex items-center gap-2">
            <svg class="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
            정산하기
          </h2>
          {#if settlement}
            <div class="flex gap-2">
              <Button variant="secondary" size="sm" loading={downloading} onclick={downloadSettlementPDF}>PDF</Button>
              <Button variant="secondary" size="sm" loading={downloading} onclick={downloadSettlementImage}>이미지</Button>
              <Button variant="secondary" size="sm" onclick={() => copyToClipboard(getSettlementSummary())}>복사</Button>
            </div>
          {/if}
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {#if settlement}
        <!-- Summary Card -->
        <div class="card mb-6">
          <h2 class="text-lg font-semibold mb-4">정산 요약</h2>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div class="p-4 bg-gradient-to-br from-primary-50 to-white rounded-xl">
              <p class="text-sm text-muted">총 지출</p>
              <p class="text-2xl font-bold text-primary">
                ₩{formatCurrency(settlement.totalExpenses)}
              </p>
            </div>
            <div class="p-4 bg-gradient-to-br from-secondary-50 to-white rounded-xl">
              <p class="text-sm text-muted">1인당 금액</p>
              <p class="text-2xl font-bold text-primary-600">
                ₩{formatCurrency(settlement.perPersonShare)}
              </p>
            </div>
            <div class="p-4 bg-gradient-to-br from-yellow-50 to-white rounded-xl">
              <p class="text-sm text-muted">참여자 수</p>
              <p class="text-2xl font-bold text-yellow-600">
                {settlement.participants.length}명
              </p>
            </div>
          </div>
        </div>

        <!-- Participant Balances -->
        <div class="card mb-6">
          <h2 class="text-lg font-semibold mb-4">참여자별 현황</h2>
          <div class="space-y-4">
            {#each settlement.participants as participant}
              <div class="flex items-center justify-between p-4 bg-app rounded-xl hover:bg-surface-hover transition-colors">
                <div class="flex items-center">
                  <div
                    class="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-lg"
                  >
                    {participant.user.name.charAt(0)}
                  </div>
                  <div class="ml-4">
                    <p class="font-medium text-primary">{participant.user.name}</p>
                    <div class="flex items-center text-sm text-muted">
                      <span>지불: ₩{formatCurrency(participant.paid)}</span>
                      <span class="mx-2">·</span>
                      <span>부담: ₩{formatCurrency(participant.owed)}</span>
                    </div>
                  </div>
                </div>
                <div class="text-right">
                  <p
                    class="text-xl font-bold {participant.balance >= 0
                      ? 'text-success'
                      : 'text-error'}"
                  >
                    {participant.balance >= 0 ? "+" : ""}₩{formatCurrency(
                      Math.abs(participant.balance)
                    )}
                  </p>
                  <p class="text-sm text-muted">
                    {participant.balance >= 0 ? "받을 금액" : "보낼 금액"}
                  </p>
                </div>
              </div>
            {/each}
          </div>
        </div>

        <!-- Settlement Transactions -->
        <div class="card mb-6">
          <h2 class="text-lg font-semibold mb-4">정산 이체 내역</h2>
          {#if settlement.transactions.length === 0}
            <div class="text-center py-12">
              <div class="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 class="text-lg font-medium text-primary">완벽한 정산!</h3>
              <p class="text-muted mt-1">모두 공평하게 지불했습니다.</p>
            </div>
          {:else}
            <div class="space-y-4">
              {#each settlement.transactions as transaction, i}
                <div
                  class="flex items-center justify-between p-4 rounded-xl border-2 border-dashed border-bright hover:border-primary-200 transition-colors"
                >
                  <div class="flex items-center">
                    <div
                      class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-error font-medium"
                    >
                      {transaction.from.name.charAt(0)}
                    </div>
                    <div class="mx-4 flex items-center">
                      <div class="w-8 h-0.5 bg-surface-hover"></div>
                      <svg class="w-4 h-4 text-muted -ml-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div
                      class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-success font-medium"
                    >
                      {transaction.to.name.charAt(0)}
                    </div>
                    <div class="ml-4">
                      <p class="text-primary">
                        <span class="font-medium">{transaction.from.name}</span>
                        <span class="text-muted mx-1">→</span>
                        <span class="font-medium">{transaction.to.name}</span>
                      </p>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-xl font-bold text-primary-600">
                      ₩{formatCurrency(transaction.amount)}
                    </p>
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </div>

        <!-- Complete Button -->
        <div class="flex justify-center">
          <Button variant="primary" size="lg" loading={loading} onclick={completeSettlement}>정산 완료하기</Button>
        </div>
      {:else}
        <div class="bg-surface rounded-xl">
          <EmptyState
            icon="expense"
            title="정산 정보 없음"
            description="지출을 먼저 기록해주세요."
          >
            <a href={`/trips/${trip.id}/expenses`} class="btn btn-primary">지출 추가하기</a>
          </EmptyState>
        </div>
      {/if}
    </main>
  </div>
</Layout>
